/**
 * @fileoverview Modal bloqueante que força o usuário a escolher um cargo
 * antes de usar o Chat Interno. Sem ESC, sem clique fora, sem botão fechar.
 * Lista cargos cadastrados em Settings → Cargos da company ativa.
 */

import React, { useEffect, useState, useCallback } from "react";
import {
  Dialog,
  Typography,
  CircularProgress,
  makeStyles
} from "@material-ui/core";
import { Briefcase, Check } from "lucide-react";
import api from "../../services/api";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { createModalDesign } from "../../styles/modalDesign";

const useStyles = makeStyles((theme) => ({
  ...createModalDesign(theme),
  intro: {
    fontSize: 13,
    color: theme.palette.type === "dark" ? "#A1A1AA" : "#52525B",
    marginBottom: 14,
    lineHeight: 1.5
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    maxHeight: 320,
    overflowY: "auto",
    paddingRight: 4
  },
  option: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 12px",
    borderRadius: 8,
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
    background: "transparent",
    color: theme.palette.type === "dark" ? "#FAFAFA" : "#09090B",
    cursor: "pointer",
    transition: "background 0.1s ease",
    textAlign: "left",
    width: "100%",
    fontSize: 14,
    "&:hover": {
      background: theme.palette.type === "dark" ? "rgba(91,124,255,0.10)" : "rgba(61,92,253,0.06)"
    }
  },
  optionSelected: {
    borderColor: theme.palette.type === "dark" ? "#5B7CFF" : "#3D5CFD",
    background: theme.palette.type === "dark" ? "rgba(91,124,255,0.16)" : "rgba(61,92,253,0.10)"
  },
  empty: {
    padding: 16,
    fontSize: 13,
    color: theme.palette.type === "dark" ? "#A1A1AA" : "#71717A",
    textAlign: "center"
  },
  loading: {
    display: "flex",
    justifyContent: "center",
    padding: 24
  }
}));

const SelectJobRoleDialog = ({ open, userId, onSaved, onNoRolesAvailable }) => {
  const classes = useStyles();
  const [jobRoles, setJobRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/job-roles");
      const list = Array.isArray(data) ? data : [];
      setJobRoles(list);
      // Sem cargos cadastrados na company: o user não tem como escolher nada
      // e o modal viraria lockout permanente (sem ESC, sem botão fechar). Avisa
      // o parent pra liberar o gate. Quando admin cadastrar cargos, o modal
      // volta a aparecer na próxima sessão.
      if (list.length === 0 && onNoRolesAvailable) {
        onNoRolesAvailable();
      }
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [onNoRolesAvailable]);

  useEffect(() => {
    if (open) {
      setSelected(null);
      fetchRoles();
    }
  }, [open, fetchRoles]);

  const handleConfirm = async () => {
    if (!selected || saving) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/users/${userId}`, { jobRoleId: selected.id });
      toast.success(i18n.t("internalChat.jobRoleRequired.toastSaved") || "Cargo definido");
      if (onSaved) onSaved(data);
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  // Bloqueante: bloqueia tanto ESC quanto clique no backdrop. onClose
  // pode receber reason "escapeKeyDown" ou "backdropClick" — ambos são
  // ignorados aqui. Antes era disableBackdropClick + disableEscapeKeyDown
  // mas essas props estão deprecated no Material UI 4.x+.
  const handleClose = (_event, reason) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") return;
    // Sem outras razões esperadas — modal só fecha programaticamente via
    // onSaved (callback de sucesso do submit).
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className={classes.mdDialog}
      maxWidth={false}
    >
      <div className={classes.mdHeader}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Briefcase size={20} strokeWidth={1.75} />
          <Typography className={classes.mdTitle}>
            {i18n.t("internalChat.jobRoleRequired.title") || "Selecione seu cargo"}
          </Typography>
        </div>
      </div>

      <div className={classes.mdContent}>
        <Typography className={classes.intro}>
          {i18n.t("internalChat.jobRoleRequired.intro")
            || "Pra usar o Chat Interno, escolha o cargo que melhor representa sua função na empresa. Ele aparecerá ao lado do seu nome nas conversas."}
        </Typography>

        {loading ? (
          <div className={classes.loading}>
            <CircularProgress size={24} />
          </div>
        ) : jobRoles.length === 0 ? (
          <div className={classes.empty}>
            {i18n.t("internalChat.jobRoleRequired.empty")
              || "Nenhum cargo cadastrado. Peça ao administrador pra cadastrar cargos em Configurações → Cargos."}
          </div>
        ) : (
          <div className={classes.list}>
            {jobRoles.map((role) => {
              const isSel = selected?.id === role.id;
              return (
                <button
                  key={role.id}
                  type="button"
                  className={`${classes.option} ${isSel ? classes.optionSelected : ""}`}
                  onClick={() => setSelected(role)}
                  disabled={saving}
                >
                  {isSel ? <Check size={15} strokeWidth={2} /> : <span style={{ width: 15 }} />}
                  <span>{role.name}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className={classes.mdActions}>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={saving || loading || !selected}
          className={classes.mdBtnPrimary}
        >
          {saving
            ? (i18n.t("internalChat.jobRoleRequired.saving") || "Salvando...")
            : (i18n.t("internalChat.jobRoleRequired.confirm") || "Confirmar")}
        </button>
      </div>
    </Dialog>
  );
};

export default SelectJobRoleDialog;
